import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Luggage } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { COLLECTIONS } from "@/lib/fleet";
import { Reveal, SectionHead } from "./Reveal";

export const Fleet = () => {
  const { t, lang } = useI18n();
  const [active, setActive] = useState("royal");
  const col = COLLECTIONS.find((c) => c.id === active);
  const colName = (c) => c.name[lang === "fr" ? "fr" : "en"];

  return (
    <section id="fleet" className="max-w-[1440px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
      <SectionHead eyebrow={t("fleet.eyebrow")} title={t("fleet.title")} />

      <Reveal delay={0.1}>
        <div className="mt-12 flex flex-wrap gap-2 border-b border-white/8">
          {COLLECTIONS.map((c) => (
            <button key={c.id} data-testid={`fleet-tab-${c.id}`} onClick={() => setActive(c.id)}
              className={`px-5 py-4 text-[0.68rem] tracking-[0.25em] uppercase transition-colors duration-400 border-b -mb-px ${active === c.id ? "text-[var(--elt-gold-light)] border-[var(--elt-gold)]" : "text-[var(--elt-muted)] border-transparent hover:text-[var(--elt-ivory)]"}`}>
              {colName(c)}
            </button>
          ))}
        </div>
      </Reveal>

      <AnimatePresence mode="wait">
        <motion.div key={active} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="mt-14 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden">
              <img src={col.image} alt={colName(col)} className="w-full aspect-[4/3] object-cover" />
              <div className="absolute inset-0 border border-[var(--elt-line)] m-4 pointer-events-none" />
            </div>
            <p className="mt-6 font-serif-elt italic text-2xl text-[var(--elt-gold-light)]">{colName(col)}</p>
            {col.partnerNetwork && (
              <p className="mt-2 text-[0.65rem] tracking-[0.25em] uppercase text-[var(--elt-muted)]">{t("fleet.partnerNetwork")}</p>
            )}
          </div>

          <div className="lg:col-span-7 flex flex-col divide-y divide-white/8">
            {col.vehicles.map((v, i) => (
              <div key={v.name} data-testid={`vehicle-card-${active}-${i}`} className="group py-7 grid sm:grid-cols-12 gap-4 items-center">
                <div className="sm:col-span-5">
                  <h3 className="font-serif-elt text-xl font-light text-[var(--elt-ivory)]">{v.name}</h3>
                  <p className="mt-1 text-[0.6rem] tracking-[0.25em] uppercase text-[var(--elt-gold)]">{v.category}{v.onRequest && ` · ${t("fleet.onRequest")}`}</p>
                  <p className="mt-2 text-xs font-light text-[var(--elt-muted)] leading-relaxed">{v.features.join(" · ")}</p>
                </div>
                <div className="sm:col-span-4 flex gap-8 text-[var(--elt-muted)]">
                  <span className="flex items-center gap-2 text-xs"><Users size={14} strokeWidth={1.3} className="text-[var(--elt-gold)]" /> {v.pax} {t("fleet.passengers")}</span>
                  <span className="flex items-center gap-2 text-xs"><Luggage size={14} strokeWidth={1.3} className="text-[var(--elt-gold)]" /> {v.luggage} {t("fleet.luggage")}</span>
                </div>
                <div className="sm:col-span-3 sm:text-right">
                  <Link to={`/request?vehicle=${encodeURIComponent(v.name)}`} data-testid={`vehicle-request-btn-${active}-${i}`}
                    className="inline-block text-[0.62rem] tracking-[0.25em] uppercase text-[var(--elt-ivory)]/70 border border-white/15 px-4 py-2.5 hover:border-[var(--elt-gold)] hover:text-[var(--elt-gold-light)] transition-colors duration-400">
                    {t("fleet.requestVehicle")}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
};
